import moment from "moment";
import { format } from "date-fns";
import { store } from "app/store";

export const formatDate = (date: number) => {
  return date ? getRelativeTime(date * 1000) : "";
};

export const getFileExtension = (filename: string): string => {
  const parts = filename.split(".");
  return parts.length > 1 ? `.${parts.pop()}` : "null";
};

export const getFileSize = (size: number): string => {
  const units = ["Bytes", "KB", "MB", "GB"];
  let i = 0;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i++;
  }
  return `${size.toFixed(2)} ${units[i]}`;
};

export const parseFileSizeToBytes = (sizeString: string): number => {
  const match = sizeString.match(/^([\d.]+)\s*(Bytes|KB|MB|GB)$/i);
  if (!match) return 0;

  const value = parseFloat(match[1]);
  const unit = match[2].toUpperCase();

  const multipliers = {
    BYTES: 1,
    KB: 1024,
    MB: 1024 * 1024,
    GB: 1024 * 1024 * 1024,
  };
  return value * (multipliers[unit as keyof typeof multipliers] || 0);
};

export const datePopOver = (time: number): string => {
  if (!time) return "";
  const datePart = formatDateValue(time);
  const timePart = format(new Date(time * 1000), "hh:mm a");
  return `${datePart}, ${timePart}`;
};

// Define a custom formatter for relative time
const relativeTimeFormatter = new Intl.RelativeTimeFormat("en", {
  numeric: "auto",
});

// Define a function to calculate the relative time from a timestamp
export const getRelativeTime = (date: number) => {
  // Get the current time in milliseconds
  const now = Date.now();
  const dateFormatted = new Date(date);
  const dateDiff = Math.floor((now - date) / (1000 * 60 * 60 * 24));

  if (dateDiff > 31) {
    return formatDateTime(dateFormatted);
  }
  // Get the difference between the current time and the timestamp in milliseconds
  const diff = now - date;
  // Define the time units and their corresponding values in milliseconds
  const units: any = {
    year: 24 * 60 * 60 * 1000 * 365,
    month: (24 * 60 * 60 * 1000 * 365) / 12,
    day: 24 * 60 * 60 * 1000,
    hour: 60 * 60 * 1000,
    minute: 60 * 1000,
    second: 1000,
  };
  // Loop through the units and find the first one that is larger than the difference
  for (let unit in units) {
    if (Math.abs(diff) > units[unit] || unit === "second") {
      // Return the formatted relative time using the unit and the difference
      return relativeTimeFormatter.format(
        Math.round(-diff / units[unit]),
        unit as Intl.RelativeTimeFormatUnit
      );
    }
  }
};

const formatDateTime = (date: Date) => {
  const dateFormat = getTeamDateFormat();
  const dateTime = moment(date)
    .format(`${dateFormat} HH:mm`)
    .replaceAll("-", "/");
  const timeZone = date
    .toLocaleString("en-US", { timeZoneName: "short" })
    .split(" ")
    .pop();
  return `${dateTime} ${timeZone}`;
};

export const getThreadId = (page: string, parentId: string) => {
  const idPrefix = ["audit", "TSK-"];
  let uid = parentId;
  idPrefix.forEach((prefix) => (uid = uid.replace(prefix, "")));
  return `arn:gaia:${page}:${uid}`;
};

export const getFileNameWithoutExtension = (filename: string): string => {
  const parts = filename.split(".");
  return parts.length > 1 ? parts.slice(0, -1).join(".") : filename;
};

export function cap(str: string): string {
  if (!str || str.length === 0) return "";
  return str[0].toUpperCase() + str.slice(1);
}

export function pluralize(word: string) {
  return word.endsWith("y") ? word.slice(0, -1) + "ies" : word + "s";
}

export function getYear(dateString?: string) {
  if (dateString) {
    for (const seg of dateString.split("-")) {
      if (seg.length === 4) return seg;
    }
  }

  return "";
}

export function getYearRange(dateStringStart?: string, dateStringEnd?: string) {
  const startYear = getYear(dateStringStart);
  const endYear = getYear(dateStringEnd);
  if (startYear === endYear) return startYear;
  if (startYear === "") return endYear;
  return `${startYear} - ${endYear}`;
}

/**
 * Helper function to get team-specific date format from Redux store
 * Returns the team's configured date format or defaults to YYYY-MM-DD
 */
export const getTeamDateFormat = (): string => {
  try {
    const state = store.getState();
    return (
      state?.teamSettings?.selectedTeamMainMetaData?.dateFormat ||
      state?.teamSettings?.currentGuestTeam?.selectedTeamMainMetaData
        ?.dateFormat ||
      "YYYY-MM-DD"
    );
  } catch (error) {
    console.warn("Store not available, using default date format");
    return "YYYY-MM-DD";
  }
};

/**
 * this should be used for displaying date outside of form field
 */
export const formatDateValue = (date: any) => {
  const dateFormat = getTeamDateFormat();
  return toDateString(date, dateFormat).replaceAll("-", "/");
};

export const formatDateValueFromDB = (value: string): string => {
  if (
    value === "1970-01-01T00:00:00Z" ||
    value === "0" ||
    value.trim() === ""
  ) {
    return "-";
  }

  // Check if the value is a Unix timestamp (number as string)
  const numericValue = Number(value);
  if (!isNaN(numericValue) && numericValue > 0) {
    const timestampInSeconds =
      numericValue > 10000000000 ? numericValue / 1000 : numericValue;
    try {
      const dateString = toDateString(timestampInSeconds);
      return dateString ? dateString.replace(/-/g, "/") : value;
    } catch (e) {
      console.error(`Error formatting timestamp: ${value}`, e);
      return value;
    }
  }

  // Check if the value is an ISO date string using regex
  const isoDateRegex =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;
  if (isoDateRegex.test(value)) {
    try {
      const timestampInSeconds = new Date(value).getTime() / 1000;
      const dateString = toDateString(timestampInSeconds);
      return dateString ? dateString.replace(/-/g, "/") : value;
    } catch (e) {
      console.error(`Error formatting ISO date: ${value}`, e);
      return value;
    }
  }

  return value;
};

/**
 * returns date in format YYYY-MM-DD
 * this should be used with datepicker/form fields
 */
export const toDateString = (date: number, format?: string) => {
  if (date) return moment.utc(date * 1000).format(format ?? "YYYY-MM-DD");
  else return "";
};

/**
 * to be used with approval
 */
export const toLocalDateString = (date: number) => {
  if (date) return new Date(date * 1000).toLocaleDateString("en-CA");
  else return "";
};

export const formatDateWithTime = (timestamp: number) => {
  return format(new Date(timestamp), "yyyy/MM/dd hh:mm a");
};

/**
 * Convert date string to start of day (00:00:00.000) in local timezone
 * Returns timestamp in milliseconds
 */
export const toStartOfDayTimestamp = (dateString?: string) => {
  if (!dateString) return 0;
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  date.setHours(0, 0, 0, 0);

  return date.getTime();
};

/**
 * Convert date string to end of day (23:59:59.999) in local timezone
 * Returns timestamp in milliseconds
 */
export const toEndOfDayTimestamp = (dateString?: string) => {
  if (!dateString) return 0;
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  date.setHours(23, 59, 59, 999);

  return date.getTime();
};

/**
 * Convert date string to end of day (23:59:59.999) in local timezone
 * Returns timestamp in seconds
 */
export const toEODUnixEpochTime = (dateString?: string) => {
  return Math.floor(toEndOfDayTimestamp(dateString) / 1000);
};

/**
 * Convert date string to noon (12:0:0.0) in UTC
 * Returns timestamp in seconds
 */
export const toUnixEpochTime = (dateString?: string) => {
  if (!dateString || dateString === undefined) return 0;

  const [year, month, day] = dateString.split("-").map(Number);
  const timestamp = Date.UTC(year, month - 1, day, 12, 0, 0, 0);

  return timestamp / 1000;
};

/**
 * Returns today's date as YYYY-MM-DD string
 */
export const getCurrentDate = () => moment().format("YYYY-MM-DD");

/**
 * Helper function to convert date strings from YYYY/MM/DD to YYYY-MM-DD format
 * for compatibility with toUnixEpochTime function
 */
export const normalizeDateFormat = (dateString: string): string => {
  if (!dateString) return dateString;
  return dateString.replace(/\//g, "-");
};
